import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Button,
  Divider,
  Alert,
  TextField,
  Chip,
  CircularProgress,
  Grid,
  Snackbar
} from '@mui/material';
import {
  NotificationsActive as NotificationsIcon,
  Add as AddIcon
} from '@mui/icons-material';
import api from '../services/api';

const ACTION_LABELS = {
  USER_DELETED: 'User Deletion',
  PASSWORD_RESET_BY_ADMIN: 'Password Reset by Admin',
  USER_CREATED: 'User Creation',
  USER_UPDATED: 'User Update',
  MULTIPLE_FAILED_LOGINS: 'Multiple Failed Login Attempts',
  CLOCK_IN: 'Clock In',
  CLOCK_OUT: 'Clock Out'
};

function AlertSettings() {
  const [settings, setSettings] = useState({});
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/alert-settings');
      
      // Convert array to object for easier handling
      const settingsObj = {};
      response.data.settings.forEach(setting => {
        settingsObj[setting.action] = {
          enabled: setting.enabled,
          priority: setting.priority,
          label: ACTION_LABELS[setting.action] || setting.action
        };
      });
      
      setSettings(settingsObj);
      setAdditionalEmails(response.data.recipients || []);
    } catch (error) {
      console.error('Error loading settings:', error);
      showNotification('Error loading settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (action) => {
    setSettings(prev => ({
      ...prev,
      [action]: {
        ...prev[action],
        enabled: !prev[action].enabled
      }
    }));
  };

  const handleAddEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newEmail && emailRegex.test(newEmail)) {
      if (!additionalEmails.includes(newEmail)) {
        setAdditionalEmails(prev => [...prev, newEmail]);
        setNewEmail('');
      } else {
        showNotification('Email already in the list', 'warning');
      }
    } else {
      showNotification('Please enter a valid email address', 'error');
    }
  };

  const handleRemoveEmail = (email) => {
    setAdditionalEmails(prev => prev.filter(e => e !== email));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      await api.put('/alert-settings', {
        settings,
        recipients: additionalEmails
      });
      
      showNotification('Settings saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const activeAlertsCount = Object.values(settings).filter(s => s.enabled).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <NotificationsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4">Alert Settings</Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Alert Triggers
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Choose which actions trigger email alerts to administrators
            </Typography>
            
            <FormGroup>
              {Object.entries(settings).map(([action, config]) => (
                <Box key={action} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enabled}
                        onChange={() => handleToggle(action)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{config.label}</span>
                        <Chip 
                          label={config.priority} 
                          size="small" 
                          color={getPriorityColor(config.priority)}
                        />
                      </Box>
                    }
                  />
                </Box>
              ))}
            </FormGroup>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Additional Recipients
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Add email addresses to receive alerts (besides administrators)
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="email@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
              />
              <Button
                variant="contained"
                onClick={handleAddEmail}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            
            <Box>
              {additionalEmails.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  onDelete={() => handleRemoveEmail(email)}
                  sx={{ m: 0.5 }}
                />
              ))}
              {additionalEmails.length === 0 && (
                <Typography color="textSecondary" variant="body2">
                  No additional recipients configured
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Alert Summary
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Active alerts: {activeAlertsCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total recipients: {additionalEmails.length} additional
            </Typography>
            <Typography variant="caption" color="textSecondary">
              (Plus all active administrators)
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AlertSettings;
